import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col, Card, InputGroup } from 'react-bootstrap';
import logo from '../../logo.png';
import axios from 'axios';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { BASE_URL, extractErrorMessage } from '../../constants/common';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { toast, ToastContainer } from "react-hot-toast";

export default function ResetPasswordConfirm() {
  const [formData, setFormData] = useState({ newPassword: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [validated, setValidated] = useState(false);
  const navigate = useNavigate();
  const { uid, token } = useParams();

  const [showPassword, setShowPassword] = useState({
    new: false,
    confirm: false,
  });

  // Real-time validation
  useEffect(() => {
    const newErrors = {};

    // New password validation
    if (touched.newPassword) {
      if (!formData.newPassword.trim()) {
        newErrors.newPassword = 'New Password is required';
      }
    }

    // Confirm password validation
    if (touched.confirmPassword) {
      if (!formData.confirmPassword.trim()) {
        newErrors.confirmPassword = 'Confirm Password is required';
      } else if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    // Real-time password match check
    if (formData.newPassword && formData.confirmPassword && touched.confirmPassword) {
      if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
  }, [formData, touched]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const resetPasswordConfirmRequest = async (uid, token, password) => {
    return await axios.post(
      `${BASE_URL}api/v1/user/reset/new/password/${uid}/${token}`,
      { password }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Mark all fields as touched for validation display
    setTouched({ newPassword: true, confirmPassword: true });
    setValidated(true);

    // Client-side validation
    const formErrors = {};
    
    if (!formData.newPassword.trim()) {
      formErrors.newPassword = 'New Password is required';
    }

    if (!formData.confirmPassword.trim()) {
      formErrors.confirmPassword = 'Confirm Password is required';
    } else if (formData.newPassword !== formData.confirmPassword) {
      formErrors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    try {
      const response = await resetPasswordConfirmRequest(uid, token, formData.newPassword);

      if (response.data?.type === 'success') {
        toast.success(response.data.message || 'Password reset successfully. You can now log in.');
        setFormData({ newPassword: '', confirmPassword: '' });
        setTouched({});
        setValidated(false);
        navigate('/login');
      } else {
        const message = response.data?.message || 'Something went wrong';
        setErrors({ confirmPassword: message });
        toast.error(message);
      }
    } catch (err) {
      const errorMessage = extractErrorMessage(err);
      toast.error(errorMessage);
      setErrors({ confirmPassword: errorMessage });
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <section className="login-content">
      <Container fluid className="h-100">
        <Row className="justify-content-center align-items-center height-self-center">
          <Col md={5} sm={12} className="align-self-center">
            <Card className="sign-user_card">
              <Card.Body>
                <div className="text-center mb-4">
                  <img src={logo} className="img-fluid rounded-normal light-logo logo" alt="logo" />
                </div>
                
                <h3 className="mb-3 text-center">Set New Password</h3>
                <p className="text-center mb-4">Enter and confirm your new password.</p>

                <Form noValidate validated={validated} onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>New Password</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPassword.new ? 'text' : 'password'}
                        name="newPassword"
                        placeholder="Enter new password"
                        value={formData.newPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.newPassword && errors.newPassword}
                        isValid={touched.newPassword && !errors.newPassword && formData.newPassword.trim()}
                        required
                      />
                      <InputGroup.Text
                        style={{ cursor: 'pointer' }}
                        onClick={() => togglePasswordVisibility('new')}
                      >
                        {showPassword.new ? <FaEyeSlash /> : <FaEye />}
                      </InputGroup.Text>
                      <Form.Control.Feedback type="invalid">
                        {errors.newPassword}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Confirm Password</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPassword.confirm ? 'text' : 'password'}
                        name="confirmPassword"
                        placeholder="Confirm new password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.confirmPassword && errors.confirmPassword}
                        isValid={
                          touched.confirmPassword && 
                          !errors.confirmPassword && 
                          formData.confirmPassword.trim() &&
                          formData.newPassword === formData.confirmPassword
                        }
                        required
                      />
                      <InputGroup.Text
                        style={{ cursor: 'pointer' }}
                        onClick={() => togglePasswordVisibility('confirm')}
                      >
                        {showPassword.confirm ? <FaEyeSlash /> : <FaEye />}
                      </InputGroup.Text>
                      <Form.Control.Feedback type="invalid">
                        {errors.confirmPassword}
                      </Form.Control.Feedback>
                      {touched.confirmPassword && 
                       !errors.confirmPassword && 
                       formData.confirmPassword.trim() &&
                       formData.newPassword === formData.confirmPassword && (
                        <Form.Control.Feedback type="valid">
                          Passwords match!
                        </Form.Control.Feedback>
                      )}
                    </InputGroup>
                  </Form.Group>

                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Link to="/login" className="text-primary">
                      Back to Login
                    </Link>
                  </div>

                  <div className="d-flex justify-content-center">
                    <Button 
                      type="submit" 
                      variant="primary" 
                      size="lg"
                      className="fw-bold"
                      disabled={Object.keys(errors).length > 0 || !formData.newPassword || !formData.confirmPassword}
                    >
                      Reset Password
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </section>
  );
}